"use client"

import {useState} from "react"
import {Controller, FormProvider, useForm, useFormContext, useWatch} from "react-hook-form"
import {useDropzone} from "react-dropzone"
import Link from "next/link"
import Script from "next/script"
import {z} from "zod"
import {zodResolver} from "@/app/lib/zodResolver"
import AppInput from "@/app/components/atomic/Input/AppInput"
import AppSelect from "@/app/components/atomic/AppSelect"
import AppButton from "@/app/components/atomic/molecules/AppButton"
import SectionTitle from "@/app/components/atomic/molecules/SectionTitle"
import {useFeatureFlag} from "@/app/hooks/useFeatureFlag"
import {useTurnstile} from "@/app/hooks/useTurnstile"
import {
    findGdprRight,
    GDPR_ACCESS_SCOPES,
    GDPR_ALL_RIGHTS_FLAG,
    GDPR_ATTACHMENT_ACCEPT,
    GDPR_ATTACHMENT_MAX_SIZE_BYTES,
    GDPR_CONFIRMATION_LINK_VALIDITY_HOURS,
    GDPR_ERASURE_INFO_BOX_TEXT,
    GDPR_ERASURE_LEGAL_NOTICE,
    GDPR_ERASURE_LEGAL_NOTICE_POLICY_LINE,
    GDPR_ERASURE_SLUG,
    GDPR_ERASURE_SUCCESS_AFTER_EMAIL,
    GDPR_ERASURE_SUCCESS_BEFORE_EMAIL,
    GDPR_ERASURE_SUCCESS_HINT,
    GDPR_ERASURE_SUCCESS_TITLE,
    GDPR_GENERIC_FOOTER,
    GDPR_GENERIC_INTRO,
    GDPR_GENERIC_TITLE,
    GDPR_INFO_BOX_TEXT,
    GDPR_OBJECTION_PROCESSINGS,
    GDPR_PORTABILITY_FORMATS,
    GDPR_PRIVACY_POLICY_PATH,
    GDPR_RECTIFICATION_FIELDS,
    GDPR_RESTRICTION_REASONS,
    GDPR_RIGHT_SLUGS,
    GDPR_RIGHTS,
    GdprAccessScope,
    GdprObjectionProcessing,
    GdprPortabilityFormat,
    GdprRectificationField,
    GdprRestrictionReason,
    GdprRightSlug,
    GdprSelectOption,
} from "@/app/constants/gdpr-rights"
import {GdprRequestDetails, GdprRequestPayload, postGdprRequest} from "@/app/endpoints/gdprEndpoints"

const TURNSTILE_ENABLED = process.env.NEXT_PUBLIC_RM_TURNSTILE_ENABLED === "1"

const REQUIRED_MESSAGE = "Ce champ est obligatoire"
const GENERIC_ERROR_MESSAGE = "Une erreur est survenue lors de l'envoi de votre demande, veuillez réessayer."
const IDENTITY_ERROR_MESSAGE = "Nous n'avons pas pu vérifier votre identité."
const IDENTITY_ERROR_HINT = "Vérifiez que le prénom, le nom et l'adresse email saisis correspondent à ceux utilisés lors de votre demande de devis."
const PRIVACY_POLICY_URL = "https://reassurez-moi.fr/guide/politique-confidentialite"

export interface GdprFormValues {
    right: GdprRightSlug | ""
    firstName: string
    lastName: string
    email: string
    identityCertified: boolean
    privacyAccepted: boolean
    accessScope: GdprAccessScope
    accessDetails: string
    rectificationField: GdprRectificationField | ""
    rectificationCurrentValue: string
    rectificationRequestedValue: string
    restrictionReason: GdprRestrictionReason | ""
    restrictionDetails: string
    portabilityFormat: GdprPortabilityFormat
    objectionProcessing: GdprObjectionProcessing | ""
    objectionReason: string
}

type FormFieldName = keyof GdprFormValues

// The erasure form is the email alone: the confirmation link proves the mailbox, the legal notice replaces the checkboxes
export const isEmailOnlyRight = (right: GdprRightSlug | ""): boolean => right === GDPR_ERASURE_SLUG

const gdprSchema = z
    .object({
        right: z.enum(GDPR_RIGHT_SLUGS, "Veuillez choisir le droit que vous souhaitez exercer"),
        firstName: z.string().trim().max(35, "35 caractères maximum"),
        lastName: z.string().trim().max(35, "35 caractères maximum"),
        email: z.string().trim().min(1, "Veuillez indiquer votre adresse mail").email("Votre email est invalide"),
        identityCertified: z.boolean(),
        privacyAccepted: z.boolean(),
        accessScope: z.string(),
        accessDetails: z.string(),
        rectificationField: z.string(),
        rectificationCurrentValue: z.string(),
        rectificationRequestedValue: z.string(),
        restrictionReason: z.string(),
        restrictionDetails: z.string(),
        portabilityFormat: z.string(),
        objectionProcessing: z.string(),
        objectionReason: z.string(),
    })
    .superRefine((values, ctx) => {
        const missing = (path: FormFieldName, message = REQUIRED_MESSAGE) => ctx.addIssue({code: "custom", path: [path], message})

        if (!isEmailOnlyRight(values.right)) {
            if (values.firstName.length < 2) missing("firstName", "Veuillez indiquer votre prénom")
            if (values.lastName.length < 2) missing("lastName", "Veuillez indiquer votre nom")
            if (!values.identityCertified) missing("identityCertified", "Vous devez certifier être la personne concernée")
            if (!values.privacyAccepted) missing("privacyAccepted", "Vous devez avoir pris connaissance de la politique de confidentialité")
        }

        switch (values.right) {
            case "acces":
                if (values.accessScope === "specific" && !values.accessDetails.trim()) missing("accessDetails", "Précisez les données souhaitées")
                break
            case "rectification":
                if (!values.rectificationField) missing("rectificationField", "Veuillez indiquer la donnée concernée")
                if (!values.rectificationRequestedValue.trim()) missing("rectificationRequestedValue", "Veuillez indiquer la valeur corrigée souhaitée")
                break
            case "limitation":
                if (!values.restrictionReason) missing("restrictionReason", "Veuillez indiquer le motif de votre demande")
                if (!values.restrictionDetails.trim()) missing("restrictionDetails", "Précisez le motif de votre demande")
                break
            case "opposition":
                if (!values.objectionProcessing) missing("objectionProcessing", "Veuillez indiquer le traitement concerné")
                if (values.objectionProcessing === "other" && !values.objectionReason.trim()) missing("objectionReason", "Précisez le motif de votre opposition")
                break
        }
    })

const DEFAULT_VALUES: GdprFormValues = {
    right: "",
    firstName: "",
    lastName: "",
    email: "",
    identityCertified: false,
    privacyAccepted: false,
    accessScope: "all",
    accessDetails: "",
    rectificationField: "",
    rectificationCurrentValue: "",
    rectificationRequestedValue: "",
    restrictionReason: "",
    restrictionDetails: "",
    portabilityFormat: "csv",
    objectionProcessing: "",
    objectionReason: "",
}

const FIELD_ORDER: readonly FormFieldName[] = [
    "right",
    "firstName",
    "lastName",
    "email",
    "accessScope",
    "accessDetails",
    "rectificationField",
    "rectificationCurrentValue",
    "rectificationRequestedValue",
    "restrictionReason",
    "restrictionDetails",
    "portabilityFormat",
    "objectionProcessing",
    "objectionReason",
    "identityCertified",
    "privacyAccepted",
]

const RIGHT_OPTIONS = GDPR_RIGHTS.map((right) => ({value: right.slug, label: right.label}))

export function buildGdprDetails(values: GdprFormValues): GdprRequestDetails {
    switch (values.right) {
        case "acces":
            return values.accessScope === "specific"
                ? {scope: "specific", scopeDetails: values.accessDetails.trim()}
                : {scope: "all"}
        case "rectification": {
            const currentValue = values.rectificationCurrentValue.trim()
            return {
                field: values.rectificationField as GdprRectificationField,
                ...(currentValue && {currentValue}),
                requestedValue: values.rectificationRequestedValue.trim(),
            }
        }
        case "limitation":
            return {reason: values.restrictionReason as GdprRestrictionReason, details: values.restrictionDetails.trim()}
        case "portabilite":
            return {format: values.portabilityFormat}
        case "opposition":
            return values.objectionProcessing === "other"
                ? {processing: "other", reason: values.objectionReason.trim()}
                : {processing: "marketing"}
        default:
            return {}
    }
}

const API_FIELD_TO_FORM_FIELD: Record<string, FormFieldName> = {
    type: "right",
    firstName: "firstName",
    lastName: "lastName",
    email: "email",
    identityCertified: "identityCertified",
    privacyAccepted: "privacyAccepted",
    scope: "accessScope",
    scopeDetails: "accessDetails",
    field: "rectificationField",
    currentValue: "rectificationCurrentValue",
    requestedValue: "rectificationRequestedValue",
    format: "portabilityFormat",
    processing: "objectionProcessing",
}

// "reason" and "details" exist in several `details` shapes, so they resolve against the active right
function apiFieldToFormField(apiField: string, right: GdprRightSlug | ""): FormFieldName | null {
    const key = apiField.replace(/^details[.[]/, "").replace(/]$/, "")
    if (key === "reason") return right === "limitation" ? "restrictionReason" : right === "opposition" ? "objectionReason" : null
    if (key === "details") return right === "limitation" ? "restrictionDetails" : null
    return API_FIELD_TO_FORM_FIELD[key] ?? null
}

const Asterisk = () => <span className="text-red-500">*</span>

function FieldError({message, dataTest}: {message?: string; dataTest?: string}) {
    if (!message) return null
    return <p className="text-[12px] text-red-600" data-test={dataTest}>{message}</p>
}

// AppInput's `type` selects a component (text, number, date…): the native type of the email field goes through inputProps
function GdprTextField({name, label, required = false, inputProps, autoComplete}: {
    name: FormFieldName
    label: string
    required?: boolean
    inputProps?: Record<string, unknown>
    autoComplete?: string
}) {
    const {control} = useFormContext<GdprFormValues>()
    return (
        <Controller
            name={name}
            control={control}
            render={({field, fieldState}) => (
                <div className="flex flex-col gap-2">
                    <label className="font-semibold text-rm-14" htmlFor={name}>{label}{required && <Asterisk />}</label>
                    <AppInput
                        id={name}
                        fieldName={name}
                        inputProps={inputProps}
                        autoComplete={autoComplete}
                        borders="neutral"
                        value={String(field.value ?? "")}
                        onChange={({value}) => field.onChange(value)}
                    />
                    <FieldError message={fieldState.error?.message} dataTest={`gdpr-error-${name}`} />
                </div>
            )}
        />
    )
}

function GdprSelectField<V extends string>({name, label, options, placeholder, required = true}: {
    name: FormFieldName
    label: string
    options: readonly GdprSelectOption<V>[]
    placeholder?: string
    required?: boolean
}) {
    const {control} = useFormContext<GdprFormValues>()
    return (
        <Controller
            name={name}
            control={control}
            render={({field, fieldState}) => (
                <div className="flex flex-col gap-2">
                    <label className="font-semibold text-rm-14" htmlFor={name}>{label}{required && <Asterisk />}</label>
                    <AppSelect
                        id={name}
                        name={name}
                        label=""
                        variant="v2"
                        placeholder={placeholder ?? "Choisissez..."}
                        value={String(field.value ?? "")}
                        options={options.map((option) => ({value: option.value, label: option.label}))}
                        onChange={(e) => field.onChange(String(e.target.value))}
                        error={fieldState.error}
                    />
                </div>
            )}
        />
    )
}

function GdprTextareaField({name, label, placeholder, required = true}: {
    name: FormFieldName
    label: string
    placeholder?: string
    required?: boolean
}) {
    const {register, formState: {errors}} = useFormContext<GdprFormValues>()
    const error = errors[name]?.message
    return (
        <div className="flex flex-col gap-2">
            <label className="font-semibold text-rm-14" htmlFor={name}>{label}{required && <Asterisk />}</label>
            <textarea
                id={name}
                placeholder={placeholder}
                className={`h-32 p-4 rounded w-full transition-all duration-500 border bg-white
                ${error ? "border-invalidRed" : "border-rm-gray-light hover:border-rm-gray focus-within:border-rm-gray"}`}
                {...register(name)}
            />
            <FieldError message={error} dataTest={`gdpr-error-${name}`} />
        </div>
    )
}

function formatFileSize(bytes: number): string {
    return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} Mo` : `${Math.max(1, Math.round(bytes / 1024))} Ko`
}

function AttachmentField({file, onChange, error}: {file: File | null; onChange: (file: File | null) => void; error?: string}) {
    const [rejection, setRejection] = useState<string | null>(null)
    const {getRootProps, getInputProps, open, isDragActive} = useDropzone({
        accept: GDPR_ATTACHMENT_ACCEPT,
        maxSize: GDPR_ATTACHMENT_MAX_SIZE_BYTES,
        multiple: false,
        noClick: true,
        noKeyboard: true,
        onDropAccepted: (files) => {
            setRejection(null)
            onChange(files[0])
        },
        onDropRejected: (rejections) => {
            const code = rejections[0]?.errors?.[0]?.code
            setRejection(code === "file-too-large" ? "Le fichier dépasse la taille maximale de 5 Mo" : "Format accepté : pdf, jpg ou png")
        },
    })

    return (
        <div className="flex flex-col gap-2">
            <span className="font-semibold text-rm-14">Justificatif (pdf, jpg, png, 5 Mo max)</span>
            <div
                {...getRootProps()}
                data-test="gdpr-attachment-dropzone"
                className={`flex flex-col md:flex-row items-center justify-between gap-3 rounded border border-dashed p-4 transition-colors
                ${isDragActive ? "border-rm-blue bg-rm-blue/10" : "border-rm-gray-light bg-white"}`}
            >
                <input {...getInputProps()} data-test="gdpr-attachment-input" name="attachment" />
                {file ? (
                    <span className="flex items-center gap-2 text-rm-14 min-w-0" data-test="gdpr-attachment-name">
                        <i className="fa-regular fa-paperclip text-rm-blue" />
                        <span className="truncate">{file.name}</span>
                        <span className="text-rm-gray shrink-0">({formatFileSize(file.size)})</span>
                    </span>
                ) : (
                    <span className="text-rm-14 text-rm-gray">Glissez votre fichier ici ou choisissez-le sur votre appareil. Recommandé pour un changement de nom.</span>
                )}
                <div className="flex gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={open}
                        data-test="gdpr-attachment-choose"
                        className="rounded-lg border border-rm-blue px-4 py-2 text-rm-14 font-semibold text-rm-blue hover:bg-rm-blue/10"
                    >
                        {file ? "Remplacer" : "Choisir un fichier"}
                    </button>
                    {file && (
                        <button
                            type="button"
                            onClick={() => {
                                setRejection(null)
                                onChange(null)
                            }}
                            data-test="gdpr-attachment-remove"
                            className="rounded-lg border border-rm-gray-light px-4 py-2 text-rm-14 text-pale hover:border-rm-gray"
                        >
                            Retirer
                        </button>
                    )}
                </div>
            </div>
            <FieldError message={rejection ?? error} dataTest="gdpr-error-attachment" />
        </div>
    )
}

function RightSpecificFields({right, attachment, onAttachmentChange, attachmentError}: {
    right: GdprRightSlug
    attachment: File | null
    onAttachmentChange: (file: File | null) => void
    attachmentError?: string
}) {
    const {control} = useFormContext<GdprFormValues>()
    const accessScope = useWatch({control, name: "accessScope"})
    const objectionProcessing = useWatch({control, name: "objectionProcessing"})

    switch (right) {
        case "acces":
            return (
                <>
                    <GdprSelectField name="accessScope" label="Périmètre de la demande" options={GDPR_ACCESS_SCOPES} />
                    {accessScope === "specific" && (
                        <GdprTextareaField name="accessDetails" label="Précisions" placeholder="Ex : uniquement mes informations de contrat" />
                    )}
                </>
            )
        case "rectification":
            return (
                <>
                    <GdprSelectField name="rectificationField" label="Donnée concernée" options={GDPR_RECTIFICATION_FIELDS} placeholder="Choisissez la donnée à corriger" />
                    <GdprTextField name="rectificationCurrentValue" label="Valeur actuelle (si connue)" />
                    <GdprTextField name="rectificationRequestedValue" label="Valeur corrigée souhaitée" required />
                    <AttachmentField file={attachment} onChange={onAttachmentChange} error={attachmentError} />
                </>
            )
        case "limitation":
            return (
                <>
                    <GdprSelectField name="restrictionReason" label="Motif de la demande" options={GDPR_RESTRICTION_REASONS} placeholder="Choisissez un motif" />
                    <GdprTextareaField name="restrictionDetails" label="Précisions" placeholder="Décrivez la donnée ou le traitement que vous contestez" />
                </>
            )
        case "portabilite":
            return <GdprSelectField name="portabilityFormat" label="Format souhaité" options={GDPR_PORTABILITY_FORMATS} required={false} />
        case "opposition":
            return (
                <>
                    <GdprSelectField name="objectionProcessing" label="Traitement concerné" options={GDPR_OBJECTION_PROCESSINGS} placeholder="Choisissez le traitement concerné" />
                    {objectionProcessing === "other" && (
                        <GdprTextareaField name="objectionReason" label="Motif de l'opposition" placeholder="Expliquez les raisons tenant à votre situation particulière" />
                    )}
                </>
            )
        default:
            return null
    }
}

function ErasureLegalNotice() {
    return (
        <div className="flex flex-col gap-2 text-[12px] leading-[18px] text-rm-gray" data-test="gdpr-legal-notice">
            {GDPR_ERASURE_LEGAL_NOTICE.map((paragraph) => (
                <p key={paragraph.slice(0, 24)}>{paragraph}</p>
            ))}
            <p>
                {GDPR_ERASURE_LEGAL_NOTICE_POLICY_LINE}{" "}
                <Link href={GDPR_PRIVACY_POLICY_PATH} target="_blank" className="text-rm-blue underline break-all">{PRIVACY_POLICY_URL}</Link>
            </p>
        </div>
    )
}

interface GdprRightsFormProps {
    initialRight?: GdprRightSlug
    // overrides the feature flag (tests); without the flag only the erasure form exists
    allRights?: boolean
}

export default function GdprRightsForm({initialRight, allRights}: GdprRightsFormProps) {
    const allRightsFlag = useFeatureFlag(GDPR_ALL_RIGHTS_FLAG)
    const showAllRights = allRights ?? allRightsFlag
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
    const [submitError, setSubmitError] = useState<{kind: "identity" | "generic"; message?: string} | null>(null)
    const [attachment, setAttachment] = useState<File | null>(null)
    const [attachmentError, setAttachmentError] = useState<string | undefined>()

    const {
        containerRef: turnstileContainerRef,
        token: turnstileToken,
        isReady: isTurnstileReady,
        reset: resetTurnstile,
    } = useTurnstile()

    const form = useForm<GdprFormValues>({
        mode: "onSubmit",
        resolver: zodResolver(gdprSchema),
        defaultValues: {...DEFAULT_VALUES, right: showAllRights ? initialRight ?? "" : GDPR_ERASURE_SLUG},
    })
    const {control, register, setValue, setError, clearErrors, formState: {errors}} = form

    const selectedSlug = useWatch({control, name: "right"})
    const right = selectedSlug ? findGdprRight(selectedSlug) : null
    const emailOnly = isEmailOnlyRight(selectedSlug)

    const handleRightChange = (slug: string) => {
        setValue("right", slug as GdprRightSlug)
        clearErrors()
        setSubmitError(null)
        setAttachment(null)
        setAttachmentError(undefined)
    }

    const onSubmit = form.handleSubmit(
        async (values) => {
            if (isSubmitting || !values.right) return
            setSubmitError(null)
            setAttachmentError(undefined)
            setIsSubmitting(true)

            const payload: GdprRequestPayload = isEmailOnlyRight(values.right)
                ? {
                    type: findGdprRight(values.right).type,
                    email: values.email.trim(),
                    turnstileToken,
                    details: buildGdprDetails(values),
                }
                : {
                    type: findGdprRight(values.right).type,
                    firstName: values.firstName.trim(),
                    lastName: values.lastName.trim(),
                    email: values.email.trim(),
                    identityCertified: true,
                    privacyAccepted: true,
                    turnstileToken,
                    details: buildGdprDetails(values),
                }

            const result = await postGdprRequest(payload, values.right === "rectification" ? attachment : null)

            // the email-only right never reveals whether the address is known: an unverified identity reads as sent
            if (result.ok || (isEmailOnlyRight(values.right) && result.error === "identity_not_verified")) {
                setSubmittedEmail(payload.email)
                setIsSubmitting(false)
                return
            }

            // Turnstile tokens are single-use: any failed attempt needs a fresh one
            resetTurnstile()

            if (result.error === "identity_not_verified") {
                setSubmitError({kind: "identity", message: result.message})
            } else if (result.error === "validation") {
                let unmapped = false
                Object.entries(result.errors).forEach(([apiField, message]) => {
                    if (apiField === "attachment") {
                        setAttachmentError(message)
                        return
                    }
                    const formField = apiFieldToFormField(apiField, values.right)
                    if (formField) setError(formField, {type: "server", message})
                    else unmapped = true
                })
                if (unmapped) setSubmitError({kind: "generic"})
            } else {
                setSubmitError({kind: "generic"})
            }
            setIsSubmitting(false)
        },
        (fieldErrors) => {
            const firstErrorField = FIELD_ORDER.find((name) => fieldErrors[name])
            if (!firstErrorField) return
            const element = document.querySelector<HTMLElement>(`[name="${firstErrorField}"]`)
            element?.scrollIntoView({behavior: "smooth", block: "center"})
            element?.focus({preventScroll: true})
        }
    )

    return (
        <div className="flex flex-col gap-rm-20" data-test="gdpr-rights-form">
            <SectionTitle level="h1" theme="white" topText={{text: "", icon: "fa-shield-check text-lg"}}>
                {right?.title ?? GDPR_GENERIC_TITLE}
            </SectionTitle>

            <div className="bg-white rounded-2xl shadow p-6 flex flex-col gap-rm-20">
                {submittedEmail ? (
                    <div className="flex flex-col items-center gap-4 text-center py-6" data-test="gdpr-success">
                        <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-green-200">
                            <i className="fa-regular fa-envelope-open-text text-green-900 text-3xl" />
                        </span>
                        {emailOnly ? (
                            <>
                                <p className="text-xl font-semibold">{GDPR_ERASURE_SUCCESS_TITLE}</p>
                                <p className="text-rm-gray">
                                    {GDPR_ERASURE_SUCCESS_BEFORE_EMAIL}<strong className="text-pale">{submittedEmail}</strong>{GDPR_ERASURE_SUCCESS_AFTER_EMAIL}
                                </p>
                                <p className="text-rm-gray">{GDPR_ERASURE_SUCCESS_HINT}</p>
                            </>
                        ) : (
                            <>
                                <p className="text-xl font-semibold">Vérifiez votre boîte mail pour confirmer votre demande</p>
                                <p className="text-rm-gray">
                                    Nous venons d'envoyer un email à <strong className="text-pale">{submittedEmail}</strong>. Cliquez sur le lien qu'il contient
                                    dans les {GDPR_CONFIRMATION_LINK_VALIDITY_HOURS} heures pour confirmer votre demande : sans confirmation, elle sera
                                    automatiquement annulée. Pensez à vérifier vos courriers indésirables.
                                </p>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        <p className="text-rm-gray" data-test="gdpr-intro">{right?.intro ?? GDPR_GENERIC_INTRO}</p>

                        <div className="flex border border-rm-blue rounded-2xl bg-rm-blue/10 p-4 gap-4">
                            <i className="fa-regular fa-info-circle text-rm-blue text-lg" />
                            <p className="text-rm-14">{emailOnly ? GDPR_ERASURE_INFO_BOX_TEXT : GDPR_INFO_BOX_TEXT}</p>
                        </div>

                        <FormProvider {...form}>
                            <form onSubmit={onSubmit} className="flex flex-col gap-rm-20" noValidate>
                                {showAllRights && (
                                    <div className="flex flex-col gap-2">
                                        <label className="font-semibold text-rm-14" htmlFor="right">Quel droit souhaitez-vous exercer ?<Asterisk /></label>
                                        <AppSelect
                                            id="right"
                                            name="right"
                                            label=""
                                            variant="v2"
                                            placeholder="Choisissez le droit à exercer"
                                            value={selectedSlug}
                                            options={RIGHT_OPTIONS}
                                            onChange={(e) => handleRightChange(String(e.target.value))}
                                            error={errors.right}
                                        />
                                    </div>
                                )}

                                {right && (
                                    <>
                                        <div className="flex flex-col gap-4">
                                            {!emailOnly && (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <GdprTextField name="firstName" label="Prénom" required autoComplete="given-name" />
                                                    <GdprTextField name="lastName" label="Nom" required autoComplete="family-name" />
                                                </div>
                                            )}

                                            <GdprTextField name="email" label="Email" required inputProps={{type: "email"}} autoComplete="email" />

                                            <RightSpecificFields
                                                right={right.slug}
                                                attachment={attachment}
                                                onAttachmentChange={(file) => {
                                                    setAttachment(file)
                                                    setAttachmentError(undefined)
                                                }}
                                                attachmentError={attachmentError}
                                            />

                                            <p className="text-rm-14 text-rm-gray">* Champs obligatoires</p>
                                        </div>

                                        {!emailOnly && (
                                            <>
                                                <div className="flex flex-col gap-1">
                                                    <label className="flex py-5 px-2.5 border border-rm-gray-light rounded items-start gap-2 text-sm text-pale cursor-pointer">
                                                        <input type="checkbox" className="mt-1 shrink-0" {...register("identityCertified")} />
                                                        <span>Je certifie être la personne concernée (ou représentant légal avec autorisation expresse)<Asterisk /></span>
                                                    </label>
                                                    <FieldError message={errors.identityCertified?.message} dataTest="gdpr-error-identityCertified" />
                                                </div>

                                                <div className="flex flex-col gap-1">
                                                    <label className="flex py-5 px-2.5 border border-rm-gray-light rounded items-start gap-2 text-sm text-pale cursor-pointer">
                                                        <input type="checkbox" className="mt-1 shrink-0" {...register("privacyAccepted")} />
                                                        <span>
                                                            J'ai pris connaissance de la{" "}
                                                            <Link href={GDPR_PRIVACY_POLICY_PATH} target="_blank" className="text-rm-blue underline">Politique de confidentialité</Link>
                                                            {" "}et je comprends que certaines données peuvent être conservées si la loi l'impose
                                                            (obligations comptables, contractuelles ou légales)<Asterisk />
                                                        </span>
                                                    </label>
                                                    <FieldError message={errors.privacyAccepted?.message} dataTest="gdpr-error-privacyAccepted" />
                                                </div>
                                            </>
                                        )}

                                        {TURNSTILE_ENABLED && (
                                            <div className="flex flex-col items-center">
                                                <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit" strategy="lazyOnload" />
                                                <div ref={turnstileContainerRef} />
                                            </div>
                                        )}

                                        {submitError?.kind === "identity" && (
                                            <div className="rounded border border-invalidRed bg-red-100 p-4 text-rm-14 text-red-900" data-test="gdpr-identity-error">
                                                <p className="font-semibold">{submitError.message ?? IDENTITY_ERROR_MESSAGE}</p>
                                                <p>{IDENTITY_ERROR_HINT}</p>
                                            </div>
                                        )}

                                        {submitError?.kind === "generic" && (
                                            <p className="text-red-700 font-semibold text-rm-14" data-test="gdpr-generic-error">{GENERIC_ERROR_MESSAGE}</p>
                                        )}

                                        <AppButton type="submit" arrowPosition="before" disabled={isSubmitting || !isTurnstileReady} wFull dataTest="gdpr-submit">
                                            {isSubmitting ? "Envoi en cours…" : "Soumettre ma demande"}
                                        </AppButton>

                                        {emailOnly && <ErasureLegalNotice />}
                                    </>
                                )}
                            </form>
                        </FormProvider>
                    </>
                )}
            </div>

            <p className="text-center text-rm-14 text-rm-gray" data-test="gdpr-footer">
                {right?.footer ?? GDPR_GENERIC_FOOTER}
            </p>
        </div>
    )
}
